import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Info, RotateCw } from "lucide-react";

import { submitOrder, uploadOrderImages } from "./fetchApi";
import { driver } from "driver.js";
import "driver.js/dist/driver.css";

import "../../styles/premium.css";
import ResultModal from "./ResultModal";
import ConfigSidebar from "./ConfigSidebar";
import LeatherPicker from "./LeatherPicker";
import PreviewColumn from "./home/PreviewColumn";
import MobilePreviewBar from "./home/MobilePreviewBar";
import { isOptionVisible } from "../../utils/conditionalLogic";
import { loadSavedStrapConfig, getCartStorageKey, getStrapConfigKey } from "../../utils/cartStorage";
import { generateStrapImages } from "../../utils/canvasGenerator";
import useAppData from "../../hooks/useAppData";
import useSessionPersistence from "../../hooks/useSessionPersistence";
import useStrapGeometry from "../../hooks/useStrapGeometry";

const apiURL = (typeof process !== "undefined" && process.env && process.env.REACT_APP_API_URL)
    ? process.env.REACT_APP_API_URL
    : "http://localhost:8000";


const Home = () => {
    const { t, i18n } = useTranslation();

    const {
        hides,
        strapOptionsList,
        localesList,
        currentLocale, setCurrentLocale,
        localCartAreas, setLocalCartAreas,
        isLoadingApp,
    } = useAppData({ i18n });

    const [isLocaleDropdownOpen, setIsLocaleDropdownOpen] = useState(false);

    // Cache loadSavedStrapConfig — gọi đúng 1 lần khi mount, tránh 2 lần parse localStorage
    const _savedConfigRef = useRef(null);
    if (_savedConfigRef.current === null) {
        _savedConfigRef.current = loadSavedStrapConfig() || {};
    }

    const [currentHideIndex, setCurrentHideIndex] = useState(() => {
        const s = sessionStorage.getItem("savedHideIndex");
        return s ? parseInt(s, 10) : 0;
    });
    const [config, setConfig] = useState(_savedConfigRef.current.config || { width: 20, buckleWidth: 20, shortLength: 80, longLength: 120 });
    const [strapSelections, setStrapSelections] = useState(_savedConfigRef.current.selections || {});

    // Tách riêng vị trí Dây Ngắn và Dây Dài
    const [shortPos, setShortPos] = useState(() => {
        const s = sessionStorage.getItem("savedShortPos");
        return s ? JSON.parse(s) : { x: 0, y: 0 };
    });
    const [longPos, setLongPos] = useState(() => {
        const s = sessionStorage.getItem("savedLongPos");
        return s ? JSON.parse(s) : { x: 0, y: 0 };
    });
    const [activePart, setActivePart] = useState(() => sessionStorage.getItem("savedActivePart") || 'short');

    const [cutOption, setCutOption] = useState(() => sessionStorage.getItem("savedCutOption") || 'preselected');

    const [strapRotation, setStrapRotation] = useState(0);

    const [preSelectedSize, setPreSelectedSize] = useState(() => {
        const s = sessionStorage.getItem("savedPreSelectedSize");
        return s ? JSON.parse(s) : null;
    });

    const [isHideFullyUsed, setIsHideFullyUsed] = useState(false);

    // QUẢN LÝ MÀN HÌNH MOBILE: tab "preview" hoặc "picker"
    const [activeMobileView, setActiveMobileView] = useState('preview');

    // Lazy init — tránh sessionStorage.getItem chạy mỗi render
    const hasSessionPos = useRef(null);
    if (hasSessionPos.current === null) {
        hasSessionPos.current = !!sessionStorage.getItem("savedPosition");
    }


    useSessionPersistence({
        currentHideIndex, shortPos, longPos, cutOption,
        activePart, preSelectedSize, strapSelections, config,
        hasSessionPosRef: hasSessionPos,
    });

    const [isConfirmed, setIsConfirmed] = useState(false);
    const [isConfigOpen, setIsConfigOpen] = useState(true);
    const [isCapturing, setIsCapturing] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isOrderFinished, setIsOrderFinished] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [capturedImageBase64, setCapturedImageBase64] = useState("");
    const [capturedHideOverviewBase64, setCapturedHideOverviewBase64] = useState("");
    const [previewScale, setPreviewScale] = useState(1);
    const [containerWidth, setContainerWidth] = useState(0);

    const imgRef = useRef(null);
    const previewContainerRef = useRef(null);
    const prevHideIdRef = useRef(null);
    const uploadPromiseRef = useRef(null);
    const prevConfigRef = useRef({ width: 20, shortLength: 80, longLength: 120 });
    const prevScaleRef = useRef(null);

    const currentHide = hides[currentHideIndex] || {};
    const currentHideId = currentHide?._id;

    const currentPrice = cutOption === 'free' ? (currentHide?.freeMovePrice || 1600) : (currentHide?.price || 0);

    // ═══════════════════════════════════════════════════════════════════════
    // HỆ THỐNG TỈ LỆ DUY NHẤT (scale)
    // ═══════════════════════════════════════════════════════════════════════
    //
    // TRƯỚC ĐÂY: Dùng 2 biến riêng strapScale=4 và imageScale=4
    //   → Dễ nhầm lẫn, không rõ ràng mối quan hệ giữa dây đeo và ảnh da
    //
    // BÂY GIỜ: Gộp thành 1 biến "scale" duy nhất = 4
    //   → Tất cả kích thước (dây đeo, ảnh da) đều dùng chung 1 hệ số
    //   → Đảm bảo tỉ lệ dây đeo : ảnh da luôn chính xác
    //
    // QUY TRÌNH CHUYỂN ĐỔI (mm → pixel trên màn hình):
    //
    //   BƯỚC 1: mm × scale = pixel NỘI BỘ (hệ toạ độ trueWidth × trueHeight)
    //     Ví dụ: Da rộng 280mm × 4 = trueWidth = 1120px (nội bộ)
    //     Ví dụ: Dây rộng 20mm × 4 = vWidth = 80px (nội bộ)
    //
    //   BƯỚC 2: pixel nội bộ × displayZoom = pixel TRÊN MÀN HÌNH
    //     displayZoom = containerWidth / trueWidth (tính trong LeatherPicker)
    //     Ví dụ: Container rộng 800px → displayZoom = 800/1120 ≈ 0.714
    //     → Da hiển thị: 1120 × 0.714 = 800px (vừa khít container) ✓
    //     → Dây hiển thị: 80 × 0.714 = 57px (đúng tỉ lệ với da) ✓
    //
    // TẠI SAO scale CỐ ĐỊNH = 4?
    //   → Vị trí kéo thả (shortPos, longPos) được lưu trong sessionStorage
    //     dưới dạng pixel NỘI BỘ. Scale cố định = vị trí luôn nhất quán.
    //   → displayZoom tự động co giãn theo container (responsive).
    //   → Tỉ lệ hiển thị cuối = scale × displayZoom = 4 × (containerW/trueW)
    //     = containerW / hideWidthMm (tự động full width)
    // ═══════════════════════════════════════════════════════════════════════

    // hideWidthMm / hideHeightMm: kích thước thật miếng da (đơn vị mm)
    // DB lưu theo cm → nhân 10 chuyển sang mm
    // Ví dụ: currentHide.width = 28 (cm) → hideWidthMm = 280 (mm)
    const hideWidthMm = currentHide?.width ? currentHide.width * 10 : 1000;
    const hideHeightMm = currentHide?.height ? currentHide.height * 10 : 1000;

    // scale: hệ số quy đổi ĐỘNG từ mm → pixel nội bộ
    // Công thức: containerWidth / hideWidthMm
    //   → 1mm da = containerWidth/hideWidthMm px nội bộ
    //   → trueWidth = hideWidthMm × scale = containerWidth (khớp khít container)
    //   → displayZoom trong LeatherPicker = containerWidth / trueWidth = 1
    // Fallback = 4 khi container chưa render (tránh chia cho 0)
    const scale = containerWidth > 0 ? containerWidth / hideWidthMm : 4;

    const {
        vWidth, vBuckleWidth, vShort, vLong, fullW, lugOff, bucOff,
        taperOffsetTop, faceGap, horizontalGap,
        currentTotalWidth, currentTotalHeight, tipH, strokeWidth,
        shortPoints, previewClipPathShort, longPath, previewClipPathLong,
        maxAssumedShort, maxAssumedLong,
    } = useStrapGeometry(config, scale);
    const previewShortPoints = shortPoints;
    const pickerShortPoints = shortPoints;
    const pickerLongPath = longPath;
    const previewLongPath = longPath;

    // ── KHUNG LUXURY PREVIEW (cột bên trái) ──────────────────────────────
    const paddingX = window.innerWidth < 768 ? 60 : 80;
    const paddingY = 80;
    const boxWidth = fullW + paddingX;
    const boxHeight = maxAssumedShort + faceGap + maxAssumedLong + paddingY;
    const anchorY = maxAssumedShort + (paddingY / 2) + (faceGap / 2);
    const previewShortLeft = paddingX / 2;
    const previewShortTop = anchorY - (faceGap / 2) - vShort;
    const previewLongLeft = paddingX / 2;
    const previewLongTop = anchorY + (faceGap / 2);

    // ── KÍCH THƯỚC ẢNH DA (leather image, mm → px) ──────────────────────
    //
    // trueWidth / trueHeight: kích thước ảnh da từ DB (pixel nội bộ)
    // Dùng cùng scale → ảnh da và dây đeo cùng hệ toạ độ
    //
    // LƯU Ý: trueHeight là chiều cao TỪ DB. Chiều cao THẬT của ảnh khi render
    // có thể khác (imgDisplayHeight) do aspect ratio tự nhiên của file ảnh.
    const trueWidth = hideWidthMm * scale;
    const trueHeight = hideHeightMm * scale;

    // imgDisplayHeight: chiều cao thực tế của ảnh khi render (tính từ natural ratio)
    // Dùng để preview texture chính xác theo vị trí trên picker
    const [imgDisplayHeight, setImgDisplayHeight] = useState(trueHeight);

    // yScale: hệ số chuyển đổi y giữa hệ "DB" (trueHeight) và hệ "display" (imgDisplayHeight)
    // Nếu ảnh khớp DB → yScale = 1. Nếu ảnh cao hơn → yScale > 1
    const yScale = trueHeight > 0 ? imgDisplayHeight / trueHeight : 1;


    const centerSelector = useCallback(() => {
        const cx = (trueWidth / 2) - (currentTotalWidth / 2);
        const cy = (trueHeight / 2) - (currentTotalHeight / 2);
        setShortPos({ x: cx, y: cy });
        setLongPos({ x: cx + vWidth + horizontalGap, y: cy });
    }, [currentTotalWidth, currentTotalHeight, trueWidth, trueHeight, vWidth, horizontalGap]);

    useEffect(() => {
        const handleResize = () => {
            const isMobile = window.innerWidth < 768;
            // Tăng diện tích sử dụng: giảm phần trừ đi để khung preview to hơn
            const availableHeight = window.innerHeight - (isMobile ? 100 : 160);
            let s = availableHeight / boxHeight;
            // Tăng scale tối đa để dây to rõ nét hơn
            if (s > 1.8) s = 1.8;
            if (s < 0.6) s = 0.6;
            setPreviewScale(s);
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [boxHeight]);

    // Reset index nếu session lưu index vượt quá số da khả dụng
    useEffect(() => {
        if (hides.length > 0 && currentHideIndex >= hides.length) {
            setCurrentHideIndex(0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hides.length]);

    // (scale cố định = 4, không cần re-center khi scale thay đổi)

    // Khi user thay đổi mm trong sidebar (free mode), giữ nguyên tâm dây đeo
    // bằng cách bù lại sự thay đổi kích thước theo công thức: delta = (prev - new) * scale / 2
    useEffect(() => {
        const prev = prevConfigRef.current;

        const dVWidth = (prev.width - config.width) * scale;
        const dVShort = (prev.shortLength - config.shortLength) * scale;
        const dVLong = (prev.longLength - config.longLength) * scale;

        const hasChange = dVWidth !== 0 || dVShort !== 0 || dVLong !== 0;

        if (hasChange && cutOption === 'free') {
            setShortPos(p => ({ x: p.x + dVWidth / 2, y: p.y + dVShort / 2 }));
            setLongPos(p => ({ x: p.x + dVWidth / 2, y: p.y + dVLong / 2 }));
        }

        prevConfigRef.current = { width: config.width, shortLength: config.shortLength, longLength: config.longLength };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [config.width, config.shortLength, config.longLength, cutOption]);

    // Khi scale thay đổi (resize cửa sổ), rescale vị trí để giữ nguyên mm trên da
    // Chỉ áp dụng cho Free Move — Preselected có snap effect riêng xử lý vị trí
    useEffect(() => {
        if (prevScaleRef.current === null) {
            prevScaleRef.current = scale;
            return;
        }
        const prevScale = prevScaleRef.current;
        if (prevScale === 0 || prevScale === scale) {
            prevScaleRef.current = scale;
            return;
        }
        if (cutOption !== 'preselected') {
            const ratio = scale / prevScale;
            setShortPos(p => ({ x: p.x * ratio, y: p.y * ratio }));
            setLongPos(p => ({ x: p.x * ratio, y: p.y * ratio }));
        }
        prevScaleRef.current = scale;
    }, [scale, cutOption]);

    // Chỉ tự động ra giữa nếu đang ở chế độ Free Move khi đổi da
    useEffect(() => {
        if (currentHideId && currentHideId !== prevHideIdRef.current) {
            if (!hasSessionPos.current || prevHideIdRef.current !== null) {
                if (cutOption === 'free') {
                    centerSelector();
                }
                hasSessionPos.current = true;
            }
            prevHideIdRef.current = currentHideId;
        }
    }, [currentHideId, centerSelector, cutOption]);

    const isCondOptionVisible = (opt, selections) => isOptionVisible(opt, selections);

    const isFormValid = useMemo(() => (strapOptionsList || [])
        .filter(opt => {
            const isReq = (opt.isRequired === true || opt.required === true || opt.isRequired === 'true' || opt.required === 'true');
            const isActive = opt.isActive !== false;
            const isVisible = opt.isHidden !== true && isCondOptionVisible(opt, strapSelections || {});
            return isReq && isActive && isVisible;
        })
        .every(opt => {
            if (!opt.key) return true;
            const val = strapSelections[opt.key];
            if (val === undefined || val === null || String(val).trim() === '' || String(val) === 'Not selected') return false;
            if (typeof val === 'string' && val.includes('||')) {
                const choiceVal = val.slice(0, val.indexOf('||'));
                const customText = val.slice(val.indexOf('||') + 2);
                const choice = (opt.choices || []).find(c => (c.value || c._id) === choiceVal);
                if (choice?.hasCustomInput && !customText.trim()) return false;
            }
            return true;
        }) && (config.width > 0 && config.shortLength > 0 && config.longLength > 0),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [strapOptionsList, strapSelections, config.width, config.shortLength, config.longLength]);

    const isSidebarLocked = isOrderFinished || isHideFullyUsed;

    const handleConfigChange = (e) => {
        if (isSidebarLocked) return;
        const { name, value } = e.target;
        const numValue = Number(value);
        setConfig(prev => ({ ...prev, [name]: numValue }));
    };

    const imageUrl = currentHide.image ? (currentHide.image.startsWith('http') ? currentHide.image : `${apiURL}/uploads/leathers/${currentHide.image}`) : '';

    // Cập nhật imgDisplayHeight khi ảnh load xong
    useEffect(() => {
        const img = imgRef.current;
        if (!img) return;
        const updateHeight = () => {
            if (img.naturalWidth > 0) {
                setImgDisplayHeight(trueWidth * (img.naturalHeight / img.naturalWidth));
            }
        };
        if (img.complete) updateHeight();
        img.addEventListener('load', updateHeight);
        return () => img.removeEventListener('load', updateHeight);
    }, [imageUrl, trueWidth]);

    const startTour = () => {
        if (window.innerWidth < 768) {
            startMobileTour();
        } else {
            startDesktopTour();
        }
    };

    const startDesktopTour = () => {
        const driverObj = driver({
            showProgress: true, animate: true,
            nextBtnText: 'Next →', prevBtnText: '← Prev', doneBtnText: 'Got it ✓',
            steps: [
                {
                    element: '#tour-step-info',
                    popover: { title: '1. Choose Your Leather', description: 'Browse available hides from this dropdown. Each leather has a unique natural pattern — select the one that suits you.', side: 'bottom', align: 'start' }
                },
                {
                    element: '#tour-step-1',
                    popover: { title: '2. Configure Dimensions', description: 'Tap to set your lug width, buckle width, and strap length to match your watch perfectly.', side: 'right' }
                },
                {
                    element: '#tour-step-cut',
                    popover: { title: '3. Choose Cut Style', description: '<b>Free Move</b> — position straps anywhere on the hide.<br><b>Pre-selected</b> — use pre-optimized zones for the best leather quality.', side: 'bottom' }
                },
                {
                    element: '#tour-step-3',
                    popover: { title: '4. Position Your Straps', description: 'Drag the <b>S</b> (short) and <b>L</b> (long) outlines to your preferred area. Use the minimap (bottom-right) to navigate.', side: 'left' }
                },
                {
                    element: '#tour-step-confirm',
                    popover: { title: '5. Confirm Your Design', description: 'Happy with the position and all options selected? Click here to capture and preview your final strap design.', side: 'left' }
                }
            ]
        });
        driverObj.drive();
    };

    const startMobileTour = () => {
        setActiveMobileView('preview');
        let driverObj;
        driverObj = driver({
            showProgress: true, animate: true,
            nextBtnText: 'Next →', prevBtnText: '← Prev', doneBtnText: 'Got it ✓',
            onNextClick: () => {
                const idx = driverObj.getActiveIndex();
                if (idx === 1) {
                    setActiveMobileView('picker');
                    setTimeout(() => driverObj.moveNext(), 400);
                    return;
                }
                driverObj.moveNext();
            },
            onPrevClick: () => {
                const idx = driverObj.getActiveIndex();
                if (idx === 2) {
                    setActiveMobileView('preview');
                    setTimeout(() => driverObj.movePrevious(), 400);
                    return;
                }
                driverObj.movePrevious();
            },
            steps: [
                {
                    element: '#tour-step-1',
                    popover: { title: '1. Configure Your Strap', description: 'Tap to customize strap dimensions: lug width, buckle width, short and long length.', side: 'right' }
                },
                {
                    element: '#tour-step-mobile-select',
                    popover: { title: '2. Browse Leathers', description: 'Tap this button to switch to the leather selection view where you can choose a hide and position your straps.', side: 'left' }
                },
                {
                    element: '#tour-step-mobile-hide',
                    popover: { title: '3. Choose a Hide', description: 'Select your preferred leather from this dropdown. Scroll down to explore the full leather canvas.', side: 'bottom' }
                },
                {
                    element: '#tour-step-3',
                    popover: { title: '4. Drag Your Straps', description: 'Drag the <b>S</b> (short) and <b>L</b> (long) outlines onto the best section of the leather. Use the minimap for quick navigation.', side: 'top' }
                },
                {
                    element: '#tour-step-mobile-confirm',
                    popover: { title: '5. Confirm Your Design', description: 'When all options are selected and position is set, tap here to finalize your strap design.', side: 'bottom' }
                }
            ]
        });
        driverObj.drive();
    };

    const handleConfirmPattern = async () => {
        setIsCapturing(true);
        try {
            const { base64Image, hideOverview } = await generateStrapImages({
                config, hideWidthMm, hideHeightMm, imageUrl,
                shortPos, longPos, scale, strapRotation,
                cutOption, preSelectedSize,
                trueWidth, trueHeight, imgDisplayHeight, yScale,
                vWidth, vBuckleWidth, vShort, vLong, tipH,
            });

            // Mở modal ngay với base64 — không chờ upload S3
            setCapturedImageBase64(base64Image);
            setCapturedHideOverviewBase64(hideOverview);
            setIsConfirmed(true);
            setIsModalOpen(true);
            setIsCapturing(false);

            // Upload S3 nền — handleSubmitOrder sẽ đợi nếu user submit sớm
            uploadPromiseRef.current = uploadOrderImages(base64Image, hideOverview)
                .then(result => {
                    if (result?.success) {
                        setCapturedImageBase64(result.mockupImageUrl);
                        setCapturedHideOverviewBase64(result.overviewImageUrl);
                    } else {
                        console.error("[ERROR] Upload images failed:", result?.error);
                    }
                    uploadPromiseRef.current = null;
                    return result;
                })
                .catch(err => {
                    console.error("[ERROR] Upload images error:", err);
                    uploadPromiseRef.current = null;
                });
        } catch (error) {
            console.error("Capture Error:", error);
            setIsCapturing(false);
        }
    };


    const handleEditPattern = () => {
        setIsModalOpen(false);
        setIsConfirmed(false);
        setCapturedImageBase64("");
        setCapturedHideOverviewBase64("");
    };

    const handleSubmitOrder = async () => {
        setIsSubmitting(true);
        // Nếu upload S3 vẫn đang chạy nền, đợi xong trước khi submit
        if (uploadPromiseRef.current) {
            await uploadPromiseRef.current;
        }
        try {
            // Chuyển đổi pixel → cm
            // Công thức: px / (scale * 10)
            //   scale chuyển mm→px, nhân 10 vì 1cm = 10mm
            //   Ví dụ: vWidth = 57px, scale = 2.857
            //     → 57 / (2.857 * 10) = 57 / 28.57 = 2.0cm = 20mm ✓
            const pxToCm = (px) => parseFloat((px / (scale * 10)).toFixed(2));
            // px → mm: chia cho scale (scale = containerWidth / hideWidthMm)
            const pxToMm = (px) => parseFloat((px / scale).toFixed(2));

            const cutCoordinates = (cutOption === 'preselected' && preSelectedSize) ? {
                // Vùng longEnd (dây dài)
                x: Math.round(preSelectedSize.xRatio * trueWidth),
                y: Math.round(preSelectedSize.yRatio * trueHeight),
                xMm: parseFloat((preSelectedSize.xRatio * hideWidthMm).toFixed(2)),
                yMm: parseFloat((preSelectedSize.yRatio * hideHeightMm).toFixed(2)),
                width: parseFloat(((preSelectedSize.wRatio * hideWidthMm) / 10).toFixed(2)),
                height: parseFloat(((preSelectedSize.hRatio * hideHeightMm) / 10).toFixed(2)),
                widthMm: parseFloat((preSelectedSize.wRatio * hideWidthMm).toFixed(2)),
                heightMm: parseFloat((preSelectedSize.hRatio * hideHeightMm).toFixed(2)),
                widthPx: Math.round(preSelectedSize.wRatio * trueWidth),
                heightPx: Math.round(preSelectedSize.hRatio * trueHeight),
                // Vùng shortEnd (dây ngắn)
                shortX: Math.round((preSelectedSize.shortXRatio ?? preSelectedSize.xRatio) * trueWidth),
                shortY: Math.round((preSelectedSize.shortYRatio ?? preSelectedSize.yRatio) * trueHeight),
                shortXMm: parseFloat(((preSelectedSize.shortXRatio ?? preSelectedSize.xRatio) * hideWidthMm).toFixed(2)),
                shortYMm: parseFloat(((preSelectedSize.shortYRatio ?? preSelectedSize.yRatio) * hideHeightMm).toFixed(2)),
                shortWidthPx: Math.round((preSelectedSize.shortWRatio ?? preSelectedSize.wRatio) * trueWidth),
                shortHeightPx: Math.round((preSelectedSize.shortHRatio ?? preSelectedSize.hRatio) * trueHeight),
                isFreeMove: false
            } : {
                short: {
                    x: Math.round(shortPos.x), y: Math.round(shortPos.y),
                    xMm: pxToMm(shortPos.x), yMm: pxToMm(shortPos.y),
                    width: pxToCm(vWidth), height: pxToCm(vShort),
                    widthMm: config.width, heightMm: config.shortLength,
                    widthPx: Math.round(vWidth), heightPx: Math.round(vShort)
                },
                long: {
                    x: Math.round(longPos.x), y: Math.round(longPos.y),
                    xMm: pxToMm(longPos.x), yMm: pxToMm(longPos.y),
                    width: pxToCm(vWidth), height: pxToCm(vLong),
                    widthMm: config.width, heightMm: config.longLength,
                    widthPx: Math.round(vWidth), heightPx: Math.round(vLong)
                },
                isFreeMove: true
            };

            // Dùng uniqueAreaId thật từ preSelectedSize (nếu có), fallback về generated ID
            const uniqueAreaId = (cutOption === 'preselected' && preSelectedSize?.uniqueAreaId)
                ? preSelectedSize.uniqueAreaId
                : (cutOption === 'preselected' && preSelectedSize?.slotIndex !== undefined)
                    ? `${currentHide?._id}_slot_${preSelectedSize.slotIndex}`
                    : `${currentHide?._id}_S${Math.round(shortPos.x)}_${Math.round(shortPos.y)}_L${Math.round(longPos.x)}_${Math.round(longPos.y)}_${Date.now().toString(36)}`;

            // Tạo bản đồ chi tiết các lựa chọn (camelCase keys)
            const strapOptions = {};

            // Thêm measurements vào đầu strapOptions
            strapOptions['leatherName'] = { label: 'Leather Name', value: currentHide?.name || '', valueLabel: currentHide?.name || '', price: 0 };
            strapOptions['lugWidth'] = { label: 'Lug Width', value: config.width, valueLabel: `${config.width}mm`, price: 0 };
            strapOptions['buckleWidth'] = { label: 'Buckle Width', value: config.buckleWidth || config.width, valueLabel: `${config.buckleWidth || config.width}mm`, price: 0 };
            strapOptions['shortLength'] = { label: 'Short Length', value: config.shortLength, valueLabel: `${config.shortLength}mm`, price: 0 };
            strapOptions['longLength'] = { label: 'Long Length', value: config.longLength, valueLabel: `${config.longLength}mm`, price: 0 };
            strapOptions['cutType'] = { label: 'Cut Type', value: cutOption === 'free' ? 'Free Move' : 'Pre-selected', valueLabel: cutOption === 'free' ? 'Free Move' : 'Pre-selected', price: 0 };
            strapOptions['areaId'] = { label: 'Area Id', value: uniqueAreaId, valueLabel: uniqueAreaId, price: 0 };

            // Bảng ánh xạ key cũ (snake_case) → key mới (camelCase)
            // Dùng làm safety net nếu DB migration chưa chạy
            const KEY_NORMALIZE = {
                'edge_finishing': 'edgeFinishing',
                'spring_bars': 'springBars',
                'tip_shape': 'tipShape',
                'test': 'curvedEnd',
                'padded_': 'padding',
                'add_your_personalization': 'personalization',
            };
            const toCamelCase = (str) => str.replace(/_([a-z0-9])/g, (_, c) => c.toUpperCase());

            (strapOptionsList || []).forEach(opt => {
                const val = strapSelections[opt.key] || '';
                let choiceVal = val;
                let customText = '';
                if (typeof val === 'string' && val.includes('||')) {
                    choiceVal = val.slice(0, val.indexOf('||'));
                    customText = val.slice(val.indexOf('||') + 2);
                }
                const choice = (opt.choices || []).find(c => (c.value || c._id) === choiceVal);
                const displayLabel = choice
                    ? (customText ? `${choice.label}: ${customText}` : choice.label)
                    : (val || 'Not selected');
                const normalizedKey = KEY_NORMALIZE[opt.key] || toCamelCase(opt.key);
                strapOptions[normalizedKey] = {
                    label: opt.label,
                    value: val,
                    valueLabel: displayLabel,
                    price: choice ? (choice.price || 0) : 0
                };
            });


            // Ảnh đã được upload lên S3 trong handleConfirmPattern, dùng URL có sẵn
            const mockupS3Url = capturedImageBase64;
            const overviewS3Url = capturedHideOverviewBase64;

            const finalOrderJSON = {
                uniqueAreaId: uniqueAreaId,
                designId: uniqueAreaId,
                leatherInfo: {
                    id: currentHide?._id,
                    name: currentHide?.name,
                    price: currentPrice
                },
                strapOptions: strapOptions, // Bao gồm measurements + strap options (camelCase keys)
                cutCoordinates: cutCoordinates,
                mockupImageUrl: mockupS3Url,
                overviewImageUrl: overviewS3Url,
                selectionType: cutOption === 'free' ? 'Free Move' : 'Pre-selected'
            };

            await submitOrder(finalOrderJSON);

            const STORAGE_KEY = getCartStorageKey();

            const savedCartStr = localStorage.getItem(STORAGE_KEY);
            let localCart = savedCartStr ? JSON.parse(savedCartStr) : [];
            // Lưu vị trí và kích thước bằng mm (scale-independent)
            // Công thức: px / scale = mm  →  khi hiển thị: mm × scale = px đúng với mọi scale
            const newItem = {
                uniqueAreaId: uniqueAreaId,
                hideId: currentHide?._id,
                isFreeMove: cutOption === 'free',
                isPreselected: cutOption === 'preselected',
                _unit: 'mm',
                ...(cutOption === 'free' ? {
                    short: {
                        x: shortPos.x / scale,
                        y: shortPos.y / scale,
                        w: config.width,
                        h: config.shortLength
                    },
                    long: {
                        x: longPos.x / scale,
                        y: longPos.y / scale,
                        w: config.width,
                        h: config.longLength
                    }
                } : {
                    // Pre-selected: lưu cả longEnd và shortEnd
                    uniqueAreaId: preSelectedSize?.uniqueAreaId,
                    longEnd: {
                        x: (preSelectedSize?.xRatio || 0) * hideWidthMm,
                        y: (preSelectedSize?.yRatio || 0) * hideHeightMm,
                        w: (preSelectedSize?.wRatio || 0) * hideWidthMm,
                        h: (preSelectedSize?.hRatio || 0) * hideHeightMm,
                    },
                    shortEnd: {
                        x: (preSelectedSize?.shortXRatio ?? preSelectedSize?.xRatio ?? 0) * hideWidthMm,
                        y: (preSelectedSize?.shortYRatio ?? preSelectedSize?.yRatio ?? 0) * hideHeightMm,
                        w: (preSelectedSize?.shortWRatio ?? preSelectedSize?.wRatio ?? 0) * hideWidthMm,
                        h: (preSelectedSize?.shortHRatio ?? preSelectedSize?.hRatio ?? 0) * hideHeightMm,
                    },
                    // Legacy fields cho backward compat
                    x: (preSelectedSize?.xRatio || 0) * hideWidthMm,
                    y: (preSelectedSize?.yRatio || 0) * hideHeightMm,
                    w: (preSelectedSize?.wRatio || 0) * hideWidthMm,
                    h: (preSelectedSize?.hRatio || 0) * hideHeightMm,
                })
            };

            localCart.push(newItem);
            localStorage.setItem(STORAGE_KEY, JSON.stringify(localCart));
            setLocalCartAreas(localCart);

            if (window.parent !== window) {
                const selectionType = cutOption === 'free' ? 'Free Move' : 'Pre-selected';
                const messagePayload = {
                    designId: uniqueAreaId,
                    uniqueAreaId: uniqueAreaId,
                    leatherId: currentHide?._id,
                    price: currentPrice,
                    selectionType: selectionType,
                    mockupImageUrl: mockupS3Url,
                    overviewImageUrl: overviewS3Url,
                    strapOptions: strapOptions,
                    coordinates: newItem
                };
                window.parent.postMessage({ type: 'ADD_TO_CART', payload: messagePayload }, "https://handdn.us");
            }

            localStorage.removeItem(getStrapConfigKey());
            setPreSelectedSize(null);
            sessionStorage.removeItem("savedPreSelectedSize");
            sessionStorage.removeItem("savedShortPos");
            sessionStorage.removeItem("savedLongPos");
            setIsModalOpen(false);
            setIsConfirmed(false);
            setIsOrderFinished(false);

        } catch (error) {
            console.error("Submit Error:", error);
            alert("Error adding to cart.");
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoadingApp) {
        return (
            <div className="position-fixed w-100 h-100 d-flex justify-content-center align-items-center" style={{ top: 0, left: 0, backgroundColor: '#FCFBF7', zIndex: 9999 }}>
                <div className="text-center d-flex flex-column align-items-center">
                    <img src="/image/logo-handdn.png" alt="Handdn" decoding="async" style={{ width: '180px', animation: 'pulseLogo 1.5s infinite ease-in-out' }} />
                    <div className="mt-4 spinner-border" style={{ color: 'rgba(0, 0, 0, 0.6)', width: '2rem', height: '2rem', borderWidth: '0.15em' }} role="status"></div>
                </div>
            </div>
        );
    }

    if (hides.length === 0) {
        return (
            <div className="w-100 h-100 d-flex justify-content-center align-items-center" style={{ backgroundColor: '#FCFBF7', minHeight: '100vh', padding: '24px' }}>
                <div className="text-center d-flex flex-column align-items-center shadow-sm p-4 p-md-5 border" style={{ backgroundColor: '#FFFFFF', borderRadius: '28px', borderColor: 'rgba(233, 228, 219, 0.8)', maxWidth: '500px', width: '100%' }}>
                    <img src="/image/logo-handdn.png" alt="Handdn" decoding="async" style={{ width: '150px', marginBottom: '32px' }} />
                    <div className="rounded-circle d-flex justify-content-center align-items-center mb-4" style={{ width: '80px', height: '80px', backgroundColor: '#FCFBF7', border: '1px dashed #C5A075' }}>
                        <Info size={32} color="#C5A075" />
                    </div>
                    <h4 className="fw-bold mb-3" style={{ color: '#1A110B', letterSpacing: '1px', fontSize: '18px' }}>
                        {t('no_leather_title', 'NO LEATHER AVAILABLE')}
                    </h4>
                    <p className="text-muted mb-4" style={{ fontSize: '14px', lineHeight: '1.6' }}>
                        {t('no_leather_desc', 'Currently, there are no leather hides available for custom styling. Please check back later.')}
                    </p>
                    <button
                        onClick={() => window.location.reload()}
                        className="btn rounded-pill fw-bold px-4 py-2 shadow-sm transition-all"
                        style={{ backgroundColor: '#C5A075', color: '#fff', border: 'none', fontSize: '13px', letterSpacing: '1px' }}
                    >
                        <RotateCw size={16} className="me-2" /> {t('btn_reload', 'RELOAD PAGE')}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid h-100 p-0 overflow-hidden position-relative" style={{ backgroundColor: '#FCFBF7' }}>
            {/* CSS ĐIỀU CHỈNH CÂN ĐỐI KHOẢNG CÁCH CHO MOBILE */}
            <style>{`
                @media (max-width: 767px) {
                    .container-fluid {
                        overflow: hidden !important;
                    }
                    .col-picker {
                        position: fixed !important;
                        top: 0;
                        right: -100%;
                        width: 100% !important;
                        height: 100% !important;
                        z-index: 1300 !important; 
                        transition: right 0.35s cubic-bezier(0.4, 0, 0.2, 1);
                        background-color: #FCFBF7;
                        display: block !important;
                    }
                    .mobile-view-picker .col-picker { right: 0 !important; }
                    .col-preview { 
                        width: 100% !important; 
                        display: flex !important; 
                    }
                    
                    /* Căn chỉnh khung bọc canvas: Ép sát lên trên, nhường khoảng trống bên dưới */
                    .mobile-canvas-wrapper {
                        padding-bottom: 90px !important; 
                        margin-top: 0px !important; 
                        align-items: flex-start !important; 
                        // overflow-y: auto !important;
                        // overflow-x: hidden !important;
                        -webkit-overflow-scrolling: touch;
                        max-height: 100dvh;
                        width: 100% !important;
                    }
                    
                    /* Scale linh hoạt và Neo (transform-origin) dính chặt vào TOP (phía trên) */
                    .preview-frame-mobile {
                        transform: scale(0.8); 
                        transform-origin: top center; 
                        margin-bottom: 40px;
                        max-width: 100vw;
                    }

                    /* Đảm bảo không mất thành phần khi màn hình quá ngắn */
                    .col-preview {
                        height: 100dvh;
                        display: flex;
                        flex-direction: column;
                        overflow: hidden;
                        width: 100% !important;
                    }
                }
            `}</style>

            {/* Result Modal - Design Finalized */}
            <ResultModal isModalOpen={isModalOpen} capturedImageBase64={capturedImageBase64} overviewImageUrl={capturedHideOverviewBase64} currentHide={currentHide} config={config} handleSubmitOrder={handleSubmitOrder} handleEditPattern={handleEditPattern} isSubmitting={isSubmitting} currentPrice={currentPrice}
                strapOptions={strapOptionsList} strapSelections={strapSelections} isFormValid={isFormValid} isCondOptionVisible={isCondOptionVisible}
                previewData={{
                    imageUrl, shortPos, longPos, trueWidth, trueHeight,
                    imgDisplayHeight, yScale,
                    boxWidth, boxHeight, vWidth, vShort, vLong, tipH, horizontalGap,
                    previewShortLeft, previewShortTop, previewLongLeft, previewLongTop,
                    previewClipPathShort, previewClipPathLong, previewShortPoints, previewLongPath,
                    strokeWidth
                }}
            /><ConfigSidebar
                isConfigOpen={isConfigOpen} setIsConfigOpen={setIsConfigOpen}
                config={config} onConfigChange={handleConfigChange} isOrderFinished={isSidebarLocked}
                strapOptions={strapOptionsList}
                strapSelections={strapSelections}
                onStrapChange={(key, value) => setStrapSelections(prev => ({ ...prev, [key]: value }))}
            />
            {isConfigOpen && <div onClick={() => setIsConfigOpen(false)} className="position-absolute w-100 h-100" style={{ zIndex: 1090, backgroundColor: 'rgba(26, 17, 11, 0.4)', backdropFilter: 'blur(3px)', cursor: 'pointer' }}></div>}

            <div className={`row g-0 h-100 mobile-view-${activeMobileView}`}>

                {/* ---------------- CỘT 1: LUXURY PREVIEW ---------------- */}
                <div className="col-lg-4 col-md-5 col-preview position-relative d-flex flex-column align-items-center h-100 overflow-hidden" style={{ backgroundColor: '#FFFFFF', borderRight: '1px solid rgba(233, 228, 219, 0.6)', paddingTop: '24px', paddingBottom: '24px' }}>
                    <PreviewColumn
                        boxWidth={boxWidth} boxHeight={boxHeight} previewScale={previewScale}
                        vWidth={vWidth} vShort={vShort} vLong={vLong} tipH={tipH}
                        fullW={fullW} lugOff={lugOff} bucOff={bucOff}
                        taperOffsetTop={taperOffsetTop} vBuckleWidth={vBuckleWidth}
                        strokeWidth={strokeWidth} scale={scale}
                        previewShortLeft={previewShortLeft} previewShortTop={previewShortTop}
                        previewLongLeft={previewLongLeft} previewLongTop={previewLongTop}
                        previewClipPathShort={previewClipPathShort} previewClipPathLong={previewClipPathLong}
                        previewShortPoints={previewShortPoints} previewLongPath={previewLongPath}
                        imageUrl={imageUrl} trueWidth={trueWidth} imgDisplayHeight={imgDisplayHeight}
                        shortPos={shortPos} longPos={longPos} yScale={yScale}
                        strapRotation={strapRotation}
                        isOrderFinished={isOrderFinished} isConfirmed={isConfirmed}
                        isConfigOpen={isConfigOpen}
                        setIsConfigOpen={setIsConfigOpen}
                        setActiveMobileView={setActiveMobileView}
                        onStartTour={startTour}
                        previewContainerRef={previewContainerRef}
                        localesList={localesList}
                        currentLocale={currentLocale}
                        setCurrentLocale={setCurrentLocale}
                        t={t}
                    />
                    <MobilePreviewBar
                        localesList={localesList}
                        currentLocale={currentLocale}
                        isLocaleDropdownOpen={isLocaleDropdownOpen}
                        setIsLocaleDropdownOpen={setIsLocaleDropdownOpen}
                        setCurrentLocale={setCurrentLocale}
                        isCapturing={isCapturing}
                        imageUrl={imageUrl}
                        isFormValid={isFormValid}
                        handleConfirmPattern={handleConfirmPattern}
                        t={t}
                    />
                </div>

                {/* ---------------- CỘT 2: LEATHER PICKER ---------------- */}
                <div id="tour-step-3" className={`col-lg-8 col-md-7 col-picker p-0 h-100`}>
                    <LeatherPicker
                        hides={hides} currentHideIndex={currentHideIndex} setCurrentHideIndex={setCurrentHideIndex}
                        imageUrl={imageUrl} imgRef={imgRef} centerSelector={centerSelector}
                        isOrderFinished={isOrderFinished} isConfirmed={isConfirmed}
                        handleConfirmPattern={handleConfirmPattern} isCapturing={isCapturing}
                        shortPos={shortPos} setShortPos={setShortPos}
                        longPos={longPos} setLongPos={setLongPos}
                        activePart={activePart} setActivePart={setActivePart}
                        vWidth={vWidth} vShort={vShort} vLong={vLong}
                        horizontalGap={horizontalGap} currentTotalWidth={currentTotalWidth} currentTotalHeight={currentTotalHeight}
                        isConfigOpen={isConfigOpen} trueWidth={trueWidth} trueHeight={trueHeight}
                        scale={scale}
                        localesList={localesList} currentLocale={currentLocale} setCurrentLocale={setCurrentLocale}
                        isLocaleDropdownOpen={isLocaleDropdownOpen} setIsLocaleDropdownOpen={setIsLocaleDropdownOpen}
                        cutOption={cutOption} setCutOption={setCutOption}
                        shortPoints={pickerShortPoints} longPath={pickerLongPath}
                        currentPrice={currentPrice} preSelectedSize={preSelectedSize} setPreSelectedSize={setPreSelectedSize}
                        localCartAreas={localCartAreas} setIsHideFullyUsed={setIsHideFullyUsed}
                        setActiveMobileView={setActiveMobileView}
                        onContainerResize={setContainerWidth}
                        isFormValid={isFormValid}
                        strapRotation={strapRotation} setStrapRotation={setStrapRotation}
                    />
                </div>

            </div>
        </div>
    );
};

export default Home;